/** Shared design-system data helpers. No sample copy lives here. */

export const ALERT_CATEGORIES = [
  "stock",
  "bom",
  "confidence",
  "dfm",
  "price",
  "setup",
] as const;

export type AlertCategory = (typeof ALERT_CATEGORIES)[number];

export type StatusTone = "teal" | "amber" | "coral";

const ALERT_TONE: Record<AlertCategory, "coral" | "amber"> = {
  stock: "coral",
  bom: "amber",
  confidence: "coral",
  dfm: "amber",
  price: "amber",
  setup: "amber",
};

export function alertTone(category: AlertCategory): "coral" | "amber" {
  return ALERT_TONE[category];
}

export function sortByAlertCategory<T extends { category: AlertCategory }>(
  items: readonly T[],
): T[] {
  return [...items].sort(
    (a, b) => ALERT_CATEGORIES.indexOf(a.category) - ALERT_CATEGORIES.indexOf(b.category),
  );
}

/**
 * The most urgent tasks, category order, capped like the stage table.
 * Callers render `rows` only. There is no "more" row.
 */
export function limitAlertTasks<T extends { category: AlertCategory }>(
  items: readonly T[],
  maxRows: number,
): { rows: T[]; total: number } {
  const sorted = sortByAlertCategory(items);
  const cap = Math.max(0, Math.floor(maxRows));
  return { rows: sorted.slice(0, cap), total: sorted.length };
}

export type StepState = "done" | "current" | "later";

export function stepperNodeState(index: number, currentIndex: number): StepState {
  if (index < currentIndex) return "done";
  if (index === currentIndex) return "current";
  return "later";
}

/** Connector after `index` is teal while it sits before the current node. */
export function stepperConnectorOn(index: number, currentIndex: number): boolean {
  return index < currentIndex;
}

/**
 * Fields the stage table is allowed to sort on. Both exist on `RfqRecord`:
 * `due_at` (customer due date) and `updated_at` (last edit). No other stamp
 * is consulted.
 */
export type StageRfqSortable = {
  due_at?: string | null;
  updated_at?: string | null;
};

function parsedTime(iso: string | null | undefined): number | null {
  if (iso == null || iso.trim() === "") return null;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? null : at;
}

/**
 * RFQs by stage.
 * 1. Rows with a parseable `due_at`, nearest first (earliest timestamp,
 *    overdue included).
 * 2. Rows with no due date after those.
 * 3. Within a group, most recently `updated_at` first. A missing or
 *    unparseable `updated_at` sorts last. Equal keys keep input order.
 * A blank or unparseable `due_at` counts as no due date.
 */
export function sortStageRfqs<T extends StageRfqSortable>(items: readonly T[]): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const aDue = parsedTime(a.item.due_at);
      const bDue = parsedTime(b.item.due_at);
      if (aDue != null && bDue != null && aDue !== bDue) return aDue - bDue;
      if (aDue != null && bDue == null) return -1;
      if (aDue == null && bDue != null) return 1;
      const aUpdated = parsedTime(a.item.updated_at);
      const bUpdated = parsedTime(b.item.updated_at);
      if (aUpdated != null && bUpdated != null && aUpdated !== bUpdated) return bUpdated - aUpdated;
      if (aUpdated != null && bUpdated == null) return -1;
      if (aUpdated == null && bUpdated != null) return 1;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

/** First `maxRows` after `sortStageRfqs`. `more` is 0 when nothing is hidden. */
export function limitStageRfqs<T extends StageRfqSortable>(
  items: readonly T[],
  maxRows: number,
): { rows: T[]; more: number } {
  const sorted = sortStageRfqs(items);
  const cap = Math.max(0, Math.floor(maxRows));
  if (sorted.length <= cap) return { rows: sorted, more: 0 };
  return { rows: sorted.slice(0, cap), more: sorted.length - cap };
}

export type SortDir = "asc" | "desc";

export type SortValue = string | number | null | undefined;

function sortValueEmpty(value: SortValue): boolean {
  return value == null || (typeof value === "string" && value.trim() === "");
}

/**
 * Sort `rows` by `value`. Empty values stay last in both directions.
 * A tie keeps the original order.
 */
export function sortByValue<T>(
  rows: readonly T[],
  value: (row: T) => SortValue,
  dir: SortDir,
): T[] {
  const sign = dir === "asc" ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const left = value(a.row);
      const right = value(b.row);
      const leftEmpty = sortValueEmpty(left);
      const rightEmpty = sortValueEmpty(right);
      if (leftEmpty && rightEmpty) return a.index - b.index;
      if (leftEmpty) return 1;
      if (rightEmpty) return -1;
      const cmp =
        typeof left === "number" && typeof right === "number"
          ? left - right
          : String(left).localeCompare(String(right), undefined, {
              numeric: true,
              sensitivity: "base",
            });
      if (cmp !== 0) return cmp * sign;
      return a.index - b.index;
    })
    .map((item) => item.row);
}

/** Same column flips direction. A new column starts ascending. */
export function toggleSort(
  currentKey: string | null,
  currentDir: SortDir,
  key: string,
): { key: string; dir: SortDir } {
  if (currentKey === key) return { key, dir: currentDir === "asc" ? "desc" : "asc" };
  return { key, dir: "asc" };
}

export const DS_SIDEBAR_STORAGE_KEY = "ds-sidebar-collapsed";

export function readSidebarCollapsed(key: string = DS_SIDEBAR_STORAGE_KEY): boolean {
  if (typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem(key) === "collapsed";
  } catch {
    return false;
  }
}

export function writeSidebarCollapsed(
  collapsed: boolean,
  key: string = DS_SIDEBAR_STORAGE_KEY,
): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(key, collapsed ? "collapsed" : "open");
  } catch {
    /* private mode / quota */
  }
}
